import React from "react";
import { useTranslation } from "react-i18next";
import {
  Commission,
  CommissionProfile,
} from "../../../core/models/entities/Commission";
import { SummaryRow } from "../OrgShell/FieldGroup";

/** Pure utility (also used outside React render): the caller can pass a
 *  translated "not set" label; defaults to the English text. */
export function formatCommission(
  commission?: Commission,
  notSetLabel = "Not set"
): string {
  if (!commission || !commission.value) return notSetLabel;
  return commission.unit === "%"
    ? `${commission.value}%`
    : `${commission.value} ${commission.unit}`;
}

interface CommissionSummaryCardProps {
  title?: string;
  commission?: CommissionProfile;
  /** Rendered top-right of the card (e.g. an "Open editor" button). */
  action?: React.ReactNode;
}

/** Compact read-only overview of a commission profile. */
export function CommissionSummaryCard({
  title,
  commission,
  action,
}: CommissionSummaryCardProps) {
  const { t } = useTranslation();
  const overrideCount =
    (commission?.typeOnly?.length ?? 0) + (commission?.methods?.length ?? 0);
  const notSet = t("compCommission.notSet");

  return (
    <div className="rounded-md border bg-gray-150 p-4">
      <div className="mb-2 flex items-center">
        <h4 className="flex-1 text-sm font-semibold text-gray-700">
          {title ?? t("compCommission.commission")}
        </h4>
        {action}
      </div>
      <SummaryRow label={t("compCommission.overallCommission")}>
        {formatCommission(commission?.overAll, notSet)}
      </SummaryRow>
      <SummaryRow label={t("compCommission.extraFees")}>
        {formatCommission(commission?.additionalFees, notSet)}
      </SummaryRow>
      <SummaryRow label={t("compCommission.heatingFees")}>
        {formatCommission(commission?.heating, notSet)}
      </SummaryRow>
      <SummaryRow label={t("compCommission.specificOverrides")}>
        {overrideCount > 0
          ? t("compCommission.typeMethodOverrides", { count: overrideCount })
          : t("compCommission.none")}
      </SummaryRow>
    </div>
  );
}
